/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import {FormattedMessage, FormattedNumber} from 'react-intl';
import PropTypes from 'prop-types';

import {useProducts} from '@salesforce/commerce-sdk-react';
import {BasketIcon} from '@salesforce/retail-react-app/app/components/icons';
import ItemVariantProvider from '@salesforce/retail-react-app/app/components/item-variant';
import CartItemVariantAttributes from '@salesforce/retail-react-app/app/components/item-variant/item-attributes';
import CartItemVariantImage from '@salesforce/retail-react-app/app/components/item-variant/item-image';
import CartItemVariantName from '@salesforce/retail-react-app/app/components/item-variant/item-name';
import CartItemVariantPrice from '@salesforce/retail-react-app/app/components/item-variant/item-price';
import Link from '@salesforce/retail-react-app/app/components/link';
import {PromoCode, usePromoCode} from '@salesforce/retail-react-app/app/components/promo-code';
import PromoPopover from '@salesforce/retail-react-app/app/components/promo-popover';
import {
  Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box, Button,
  Divider, Flex, Heading, Stack, Text,
} from '@salesforce/retail-react-app/app/components/shared/ui';

const CartItems = ({basket}) => {
  const totalItems = basket?.productItems?.reduce((acc, item) => acc + item.quantity, 0) || 0;
  const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? '';
  const {data: products} = useProducts(
    {
      parameters: {
        ids: productIds,
        allImages: true,
      },
    },
    {
      enabled: Boolean(productIds),
      select: (result) => {
        // Convert array into key/value object with key is the product id
        return result?.data?.reduce((result, item) => {
          const key = item.id;

          result[key] = item;

          return result;
        }, {});
      },
    }
  );

  return (
    <Accordion allowToggle={true} width="100%">
      <AccordionItem style={{border: 0}}>
        <AccordionButton color="blue.700">
          <BasketIcon aria-hidden={true} />
          <Box px={2}>
            <FormattedMessage
              defaultMessage="{itemCount, plural, =0 {0 items} one {# item} other {# items}} in cart"
              description="clicking it would expand/show the items in cart"
              id="order_summary.cart_items.action.num_of_items_in_cart"
              values={{itemCount: totalItems}}
            />
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel px={0} py={4}>
          <Stack align="flex-start" divider={<Divider />} spacing={5}>
            {basket.productItems?.map((product, idx) => {
              const variant = {
                ...product,
                ...(products && products[product.productId]),
                price: product.price,
              };

              return (
                <ItemVariantProvider
                  key={product.productId}
                  index={idx}
                  variant={variant}
                >
                  <Flex alignItems="flex-start" width="full">
                    <CartItemVariantImage mr={2} width="80px" />
                    <Stack marginTop="-3px" spacing={1} width="full">
                      <CartItemVariantName />
                      <CartItemVariantAttributes includeQuantity />
                      <CartItemVariantPrice
                        baseDirection="row"
                        currency={basket?.currency}
                      />
                    </Stack>
                  </Flex>
                </ItemVariantProvider>
              );
            })}

            <Button as={Link} color="blue.700" to="/cart" variant="link" width="full">
              <FormattedMessage
                defaultMessage="Edit cart"
                id="order_summary.cart_items.link.edit_cart"
              />
            </Button>
          </Stack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

CartItems.propTypes = {
  basket: PropTypes.object,
};

const OrderSummary = ({
  basket,
  showPromoCodeForm = false,
  showCartItems = false,
  isEstimate = false,
  fontSize = 'md',
}) => {
  const {removePromoCode, ...promoCodeProps} = usePromoCode();

  if (!basket?.basketId && !basket?.orderNo) {
    return null;
  }
  const shippingItem = basket.shippingItems?.[0];
  const hasShippingPromos = shippingItem?.priceAdjustments?.length > 0;

  return (
    <Stack data-testid="sf-order-summary" spacing={5}>
      <Heading fontSize={fontSize} id="order-summary-heading" pt={1}>
        <FormattedMessage
          defaultMessage="Order Summary"
          id="order_summary.heading.order_summary"
        />
      </Heading>

      <Stack
        align="flex-start"
        aria-labelledby="order-summary-heading"
        role="region"
        spacing={4}
      >
        {showCartItems && <CartItems basket={basket} />}

        <Stack w="full">
          <Flex aria-atomic="true" aria-live="polite" justify="space-between">
            <Text fontSize={fontSize} fontWeight="bold">
              <FormattedMessage
                defaultMessage="Subtotal"
                id="order_summary.label.subtotal"
              />
            </Text>
            <Text fontSize={fontSize} fontWeight="bold">
              <FormattedNumber
                currency={basket?.currency}
                style="currency"
                value={basket?.productSubTotal}
              />
            </Text>
          </Flex>

          {basket.orderPriceAdjustments?.map((adjustment) => (
            <Flex
              key={adjustment.priceAdjustmentId}
              aria-atomic="true"
              aria-live="polite"
              justify="space-between"
            >
              <Text fontSize={fontSize}>{adjustment.itemText}</Text>
              <Text color="green.600" fontSize={fontSize}>
                <FormattedNumber
                  currency={basket?.currency}
                  style="currency"
                  value={adjustment.price}
                />
              </Text>
            </Flex>
          ))}

          <Flex aria-atomic="true" aria-live="polite" justify="space-between">
            <Flex alignItems="center">
              <Text fontSize={fontSize} lineHeight={1}>
                <FormattedMessage
                  defaultMessage="Shipping"
                  id="order_summary.label.shipping"
                />
                {hasShippingPromos && (
                  <Text as="span" ml={1}>
                    (
                    <FormattedMessage
                      defaultMessage="Promotion applied"
                      id="order_summary.label.promo_applied"
                    />
                    )
                  </Text>
                )}
              </Text>
              {hasShippingPromos && (
                <PromoPopover ml={1}>
                  <Stack>
                    {shippingItem?.priceAdjustments?.map((adjustment) => (
                      <Text key={adjustment.priceAdjustmentId} fontSize="sm">
                        {adjustment.itemText}
                      </Text>
                    ))}
                  </Stack>
                </PromoPopover>
              )}
            </Flex>

            {shippingItem?.priceAdjustments?.some(
              ({appliedDiscount}) => appliedDiscount?.type === 'free'
            ) ? (
                <Text
                  as="span"
                  color="green.700"
                  fontSize={fontSize}
                  textTransform="uppercase"
                >
                  <FormattedMessage
                    defaultMessage="Free"
                    id="order_summary.label.free"
                  />
                </Text>
              ) : (
                <Text fontSize={fontSize}>
                  <FormattedNumber
                    currency={basket.currency}
                    style="currency"
                    value={basket.shippingTotal}
                  />
                </Text>
              )}
          </Flex>

          <Flex aria-atomic="true" aria-live="polite" justify="space-between">
            <Text fontSize={fontSize}>
              <FormattedMessage defaultMessage="Tax" id="order_summary.label.tax" />
            </Text>
            {basket.taxTotal != null ? (
              <Text fontSize={fontSize}>
                <FormattedNumber
                  currency={basket.currency}
                  style="currency"
                  value={basket.taxTotal}
                />
              </Text>
            ) : (
              <Text color="gray.700" fontSize={fontSize}>
                TBD
              </Text>
            )}
          </Flex>
        </Stack>

        {showPromoCodeForm ? (
          <Box w="full">
            <PromoCode {...promoCodeProps} />
          </Box>
        ) : (
          <Divider />
        )}

        <Stack spacing={4} w="full">
          <Flex aria-atomic="true" aria-live="polite" justify="space-between" w="full">
            {isEstimate ? (
              <Text fontSize={fontSize} fontWeight="bold">
                <FormattedMessage
                  defaultMessage="Estimated Total"
                  id="order_summary.label.estimated_total"
                />
              </Text>
            ) : (
              <Text fontSize={fontSize} fontWeight="bold">
                <FormattedMessage
                  defaultMessage="Order Total"
                  id="order_summary.label.order_total"
                />
              </Text>
            )}
            <Text fontSize={fontSize} fontWeight="bold">
              <FormattedNumber
                currency={basket?.currency}
                style="currency"
                value={basket?.orderTotal || basket?.productTotal}
              />
            </Text>
          </Flex>

          {basket.couponItems?.length > 0 && (
            <Stack
              bg="white"
              border="1px solid"
              borderColor="gray.100"
              borderRadius="base"
              p={4}
            >
              <Text fontSize={fontSize} fontWeight="medium">
                <FormattedMessage
                  defaultMessage="Promotions applied"
                  id="order_summary.label.promotions_applied"
                />
                :
              </Text>
              <Stack>
                {basket.couponItems.map((item) => (
                  <Flex key={item.couponItemId} alignItems="center">
                    <Text color="gray.800" flex="1" fontSize="sm">
                      {item.code}
                    </Text>
                    {!basket.orderNo && (
                      <Button
                        colorScheme="red"
                        size="sm"
                        variant="link"
                        onClick={() => removePromoCode(item.couponItemId)}
                      >
                        <FormattedMessage
                          defaultMessage="Remove"
                          id="order_summary.action.remove_promo"
                        />
                      </Button>
                    )}
                  </Flex>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

OrderSummary.propTypes = {
  basket: PropTypes.object,
  showPromoCodeForm: PropTypes.bool,
  showCartItems: PropTypes.bool,
  isEstimate: PropTypes.bool,
  fontSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
};

export default OrderSummary;
